import {
	ComIbmCommerceUserBeansCountryStateListDataBeanIBMCountryStateListSummary,
	ComIbmCommerceUserBeansCountryStateListDataBeanIBMCountryStateName,
} from './data-contracts';
import { HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class Country<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * @description Gets a list of countries or regions with the corresponding states or provinces.
	 *
	 * @tags Country
	 * @name CountryFindCountryStateList
	 * @summary Get countries and regions
	 * @request GET:/store/{storeId}/country/country_state_list
	 * @secure
	 * @response `200` `ComIbmCommerceUserBeansCountryStateListDataBeanIBMCountryStateListSummary` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	countryFindCountryStateList = (
		storeId: string,
		query?: {
			/** Profile name. Profiles determine the subset of data returned by a query.  Default profile name = IBM_countryStateList_Summary. */
			profileName?: 'IBM_countryStateList_Summary';
			/** The country or region abbreviation code. */
			countryCode?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['countryFindCountryStateList'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/country/country_state_list`,
				params: _params,
				query: query,
				methodName: 'countryFindCountryStateList',
			};
			logger.trace(traceData);
		}
		return this.http.request<
			ComIbmCommerceUserBeansCountryStateListDataBeanIBMCountryStateListSummary,
			void
		>({
			path: `/store/${storeId}/country/country_state_list`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Gets the display name of a country and/or a state.
	 *
	 * @tags Country
	 * @name CountryFindCountryStateName
	 * @summary Get display name of countries and regions
	 * @request GET:/store/{storeId}/country/country_state_name
	 * @secure
	 * @response `200` `ComIbmCommerceUserBeansCountryStateListDataBeanIBMCountryStateName` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	countryFindCountryStateName = (
		storeId: string,
		query?: {
			/** Profile name. Profiles determine the subset of data returned by a query. The default profile name is IBM_countryStateName. */
			profileName?: 'IBM_countryStateName';
			/** The country or region abbreviation code. */
			countryCode?: string;
			/** The state or province abbreviation code. */
			stateCode?: string;
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['countryFindCountryStateName'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/country/country_state_name`,
				params: _params,
				query: query,
				methodName: 'countryFindCountryStateName',
			};
			logger.trace(traceData);
		}
		return this.http.request<
			ComIbmCommerceUserBeansCountryStateListDataBeanIBMCountryStateName,
			void
		>({
			path: `/store/${storeId}/country/country_state_name`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
